// SPDX-License-Identifier: Elastic-2.0
/**
 * Sidebar — top-level navigation shell.
 *
 * Generic + content-agnostic. The caller assembles `<SidebarSection>` and
 * `<SidebarItem>` children (and optional account chip / footer button) —
 * T-020 wires the actual Mirlai entries (Devices, Routines, Suggestions,
 * Settings, Advanced) per functional spec § 4.5.
 *
 * Width: 264px expanded / 64px collapsed. The collapse transition is purely
 * visual — caller owns the toggle state via `collapsed` + `onCollapsedChange`.
 *
 * The `collapsed` class is applied at the root so descendant selectors
 * (`.collapsed .itemLabel { display: none }`, etc.) hide text reliably
 * without each child needing to subscribe to context.
 */
import type { HTMLAttributes, ReactNode } from "react";

import styles from "./Sidebar.module.css";

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  /** Children: typically an account chip, scrolled `<SidebarSection>`s, and an optional footer button. */
  children?: ReactNode;
  /** When true, sidebar shrinks to 64px and items hide labels (icons only). */
  collapsed?: boolean;
  /**
   * Toggle callback; accepted so the caller can wire its own collapse
   * control. The primitive itself does NOT render a built-in toggle —
   * that's a downstream concern.
   */
  onCollapsedChange?: (next: boolean) => void;
}

export function Sidebar({
  collapsed = false,
  // Accepted for future-proofing (T-020 may render a collapse button); the
  // primitive itself does not render a toggle.
  onCollapsedChange: _onCollapsedChange,
  className,
  children,
  ...rest
}: SidebarProps): JSX.Element {
  const composed = [styles.sidebar, collapsed ? styles.collapsed : null, className]
    .filter(Boolean)
    .join(" ");
  return (
    <aside
      className={composed}
      data-collapsed={collapsed ? "true" : "false"}
      aria-label={rest["aria-label"] ?? "Primary navigation"}
      {...rest}
    >
      <div className={styles.scroll}>{children}</div>
    </aside>
  );
}
